/**
 * Influencer Link page — per-creator unique link management UI.
 *
 * Renders:
 *   - Create form: influencer_id + destination_url + optional 8-char slug
 *   - Links table: GET /by-influencer/:id — slug, destination, revoked badge
 *   - Copy-link button per row (short URL at https://go.trackerity.com/<slug>)
 *   - Revoke button (POST /revoke/:id) — greys the row + "Revoked" badge
 *   - Delete button (DELETE /:id)
 *
 * Data layer: TanStack Query for fetch + mutations, Sonner for feedback.
 * Form layer: local state + hand-rolled validator in `./api.ts` mirroring
 * a Zod-style `safeParse`. Keeps this feature zero-dep on react-hook-form
 * for the lane.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	buildShortUrl,
	createLink,
	createLinkFormSchema,
	deleteLink,
	fetchLinksByInfluencer,
	influencerLinkQueryKeys,
	type InfluencerLinkRow,
	isLinkRevoked,
	revokeLink,
	SHORT_LINK_HOST,
	SLUG_LEN,
} from "./api";

// ---------------------------------------------------------------------------
// Local UI state — compact, reducer-free. The create form is ephemeral and
// resets on success, so there's no need to lift into a query cache.
// ---------------------------------------------------------------------------

interface CreateFormState {
	influencerId: string;
	destinationUrl: string;
	customSlug: string;
}

const EMPTY_CREATE_FORM: CreateFormState = {
	influencerId: "",
	destinationUrl: "",
	customSlug: "",
};

// ---------------------------------------------------------------------------
// Static style tokens — mirrors OsTargetingForm: light theme, zinc borders,
// 6-8px rounded corners, emerald primary, sky accents, no emojis.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const PRIMARY_BTN =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_BTN =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const DANGER_BTN =
	"rounded-[6px] border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page props — `initialInfluencerId` lets the parent route prefill the
// lookup field (via `?influencerId=` search param). When present, the links
// table auto-queries; when absent, the operator types the id manually.
// ---------------------------------------------------------------------------

export interface InfluencerLinkPageProps {
	initialInfluencerId?: string;
}

export function InfluencerLinkPage({
	initialInfluencerId,
}: InfluencerLinkPageProps = {}) {
	const queryClient = useQueryClient();

	const [createForm, setCreateForm] = useState<CreateFormState>({
		...EMPTY_CREATE_FORM,
		influencerId: initialInfluencerId ?? "",
	});
	const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

	// The lookup field drives the table. Defaults to the create-form value
	// so creating + listing feels like one flow, but operators can override.
	const [lookupId, setLookupId] = useState<string>(initialInfluencerId ?? "");

	const trimmedLookupId = lookupId.trim();
	const canQuery = trimmedLookupId.length > 0;

	// ---------------- Query: list links by influencer ----------------
	const linksQuery = useQuery({
		queryKey: influencerLinkQueryKeys.byInfluencer(trimmedLookupId),
		queryFn: () => fetchLinksByInfluencer(trimmedLookupId),
		enabled: canQuery,
	});

	// ---------------- Mutations ----------------
	const createMutation = useMutation({
		mutationFn: createLink,
		onSuccess: (row) => {
			toast.success(`Link created. Short URL: ${buildShortUrl(row.slug)}`);
			queryClient.invalidateQueries({
				queryKey: influencerLinkQueryKeys.byInfluencer(row.influencer_id),
			});
			// Reset the form (keep the influencer id to allow batch creation).
			setCreateForm((prev) => ({
				...EMPTY_CREATE_FORM,
				influencerId: prev.influencerId,
			}));
			setCreateErrors({});
			// If the lookup field is empty, sync it so the operator sees the new row.
			if (!canQuery) setLookupId(row.influencer_id);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to create link.");
		},
	});

	const revokeMutation = useMutation({
		mutationFn: (id: string) => revokeLink(id),
		onSuccess: (row) => {
			toast.success("Link revoked.");
			queryClient.setQueryData<InfluencerLinkRow[] | undefined>(
				influencerLinkQueryKeys.byInfluencer(row.influencer_id),
				(prev) =>
					prev
						? prev.map((r) => (r.id === row.id ? { ...r, ...row } : r))
						: prev,
			);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to revoke link.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteLink(id),
		onSuccess: (_void, id) => {
			toast.success("Link deleted.");
			queryClient.setQueryData<InfluencerLinkRow[] | undefined>(
				influencerLinkQueryKeys.byInfluencer(trimmedLookupId),
				(prev) => (prev ? prev.filter((r) => r.id !== id) : prev),
			);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to delete link.");
		},
	});

	// ---------------- Handlers ----------------
	function updateCreateField<K extends keyof CreateFormState>(
		key: K,
		value: CreateFormState[K],
	) {
		setCreateForm((prev) => ({ ...prev, [key]: value }));
		if (createErrors[key as string]) {
			setCreateErrors((prev) => {
				const next = { ...prev };
				delete next[key as string];
				return next;
			});
		}
	}

	function handleCreateSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = createLinkFormSchema.safeParse({
			influencerId: createForm.influencerId,
			destinationUrl: createForm.destinationUrl,
			customSlug: createForm.customSlug,
		});
		if (!parsed.success) {
			setCreateErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setCreateErrors({});
		createMutation.mutate({
			influencerId: parsed.data.influencerId,
			destinationUrl: parsed.data.destinationUrl,
			customSlug: parsed.data.customSlug,
		});
	}

	function handleLookupSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		// The query re-runs on state change; this handler only exists so the
		// form has a submit affordance for keyboard users.
	}

	const busy =
		createMutation.isPending ||
		revokeMutation.isPending ||
		deleteMutation.isPending;

	return (
		<div className="flex flex-col gap-8">
			<CreateLinkCard
				form={createForm}
				errors={createErrors}
				busy={createMutation.isPending}
				onChange={updateCreateField}
				onSubmit={handleCreateSubmit}
			/>

			<LookupCard
				lookupId={lookupId}
				onChangeLookupId={setLookupId}
				onSubmit={handleLookupSubmit}
				loading={linksQuery.isFetching}
			/>

			<LinksTableCard
				rows={linksQuery.data ?? []}
				canQuery={canQuery}
				loading={linksQuery.isLoading || linksQuery.isFetching}
				error={linksQuery.error instanceof Error ? linksQuery.error.message : null}
				busy={busy}
				onRevoke={(id) => revokeMutation.mutate(id)}
				onDelete={(id) => deleteMutation.mutate(id)}
				pendingRevokeId={
					revokeMutation.isPending
						? (revokeMutation.variables as string | undefined) ?? null
						: null
				}
				pendingDeleteId={
					deleteMutation.isPending
						? (deleteMutation.variables as string | undefined) ?? null
						: null
				}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Create-link card
// ---------------------------------------------------------------------------

function CreateLinkCard(props: {
	form: CreateFormState;
	errors: Record<string, string>;
	busy: boolean;
	onChange: <K extends keyof CreateFormState>(
		key: K,
		value: CreateFormState[K],
	) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}) {
	return (
		<section className={`${CARD} p-6`} aria-labelledby="create-link-heading">
			<div className="mb-4 flex flex-col gap-1">
				<h2 id="create-link-heading" className="text-lg font-semibold text-zinc-900">
					Create a link
				</h2>
				<p className={HINT}>
					Each influencer can own many short links. The tracker mints an 8-character
					slug unless you pass a custom one.
				</p>
			</div>
			<form
				onSubmit={props.onSubmit}
				className="flex flex-col gap-4"
				aria-label="Create influencer link"
				noValidate
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="create-influencer-id" className={LABEL}>
						Influencer ID
					</label>
					<input
						id="create-influencer-id"
						name="influencerId"
						type="text"
						autoComplete="off"
						placeholder="e.g. inf_ava42"
						className={INPUT}
						value={props.form.influencerId}
						onChange={(e) => props.onChange("influencerId", e.currentTarget.value)}
						aria-invalid={Boolean(props.errors.influencerId)}
						aria-describedby={
							props.errors.influencerId ? "create-influencer-id-error" : undefined
						}
					/>
					{props.errors.influencerId && (
						<p id="create-influencer-id-error" className={ERROR_TEXT}>
							{props.errors.influencerId}
						</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="create-destination-url" className={LABEL}>
						Destination URL
					</label>
					<input
						id="create-destination-url"
						name="destinationUrl"
						type="url"
						inputMode="url"
						autoComplete="off"
						placeholder="https://example.com/landing"
						className={INPUT}
						value={props.form.destinationUrl}
						onChange={(e) =>
							props.onChange("destinationUrl", e.currentTarget.value)
						}
						aria-invalid={Boolean(props.errors.destinationUrl)}
						aria-describedby={
							props.errors.destinationUrl
								? "create-destination-url-error"
								: "create-destination-url-hint"
						}
					/>
					{props.errors.destinationUrl ? (
						<p id="create-destination-url-error" className={ERROR_TEXT}>
							{props.errors.destinationUrl}
						</p>
					) : (
						<p id="create-destination-url-hint" className={HINT}>
							Only http(s) URLs are accepted. Trailing slashes and query strings
							are fine.
						</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="create-custom-slug" className={LABEL}>
						Custom slug <span className="font-normal text-zinc-400">(optional)</span>
					</label>
					<input
						id="create-custom-slug"
						name="customSlug"
						type="text"
						autoComplete="off"
						placeholder={`exactly ${SLUG_LEN} chars — letters, digits, _ or -`}
						className={`${INPUT} max-w-xs`}
						value={props.form.customSlug}
						onChange={(e) => props.onChange("customSlug", e.currentTarget.value)}
						aria-invalid={Boolean(props.errors.customSlug)}
						aria-describedby={
							props.errors.customSlug
								? "create-custom-slug-error"
								: "create-custom-slug-hint"
						}
					/>
					{props.errors.customSlug ? (
						<p id="create-custom-slug-error" className={ERROR_TEXT}>
							{props.errors.customSlug}
						</p>
					) : (
						<p id="create-custom-slug-hint" className={HINT}>
							Leave empty to auto-generate. Collisions return a 409 inline.
						</p>
					)}
				</div>

				<div className="flex items-center justify-end border-t border-zinc-200 pt-4">
					<button
						type="submit"
						disabled={props.busy}
						className={PRIMARY_BTN}
					>
						{props.busy ? "Creating." : "Create link"}
					</button>
				</div>
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Lookup card — drives the table's influencer filter.
// ---------------------------------------------------------------------------

function LookupCard(props: {
	lookupId: string;
	onChangeLookupId: (v: string) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	loading?: boolean;
}) {
	return (
		<section className={`${CARD} p-6`} aria-labelledby="lookup-heading">
			<div className="mb-4 flex flex-col gap-1">
				<h2 id="lookup-heading" className="text-lg font-semibold text-zinc-900">
					Links by influencer
				</h2>
				<p className={HINT}>
					Enter an influencer ID to list and manage their short links.
				</p>
			</div>
			<form
				onSubmit={props.onSubmit}
				className="flex flex-col gap-3 sm:flex-row sm:items-end"
				aria-label="Look up links by influencer"
			>
				<div className="flex flex-1 flex-col gap-1.5">
					<label htmlFor="lookup-influencer-id" className={LABEL}>
						Influencer ID
					</label>
					<input
						id="lookup-influencer-id"
						name="lookupInfluencerId"
						type="text"
						autoComplete="off"
						placeholder="e.g. inf_ava42"
						className={INPUT}
						value={props.lookupId}
						onChange={(e) => props.onChangeLookupId(e.currentTarget.value)}
					/>
				</div>
				<div className="flex items-center gap-2">
					{props.loading && (
						<span className={HINT} aria-live="polite">
							Loading links.
						</span>
					)}
				</div>
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Links table card
// ---------------------------------------------------------------------------

function LinksTableCard(props: {
	rows: InfluencerLinkRow[];
	canQuery: boolean;
	loading: boolean;
	error: string | null;
	busy: boolean;
	onRevoke: (id: string) => void;
	onDelete: (id: string) => void;
	pendingRevokeId: string | null;
	pendingDeleteId: string | null;
}) {
	const sortedRows = useMemo(
		() =>
			props.rows.slice().sort((a, b) => b.created_at - a.created_at),
		[props.rows],
	);

	return (
		<section className={`${CARD} overflow-hidden`} aria-labelledby="links-table-heading">
			<header className="flex flex-col gap-1 border-b border-zinc-200 bg-zinc-50 px-6 py-4">
				<h2
					id="links-table-heading"
					className="text-lg font-semibold text-zinc-900"
				>
					Managed links
				</h2>
				<p className={HINT}>
					Short URLs follow the <code className="rounded bg-zinc-100 px-1 py-0.5 text-xs text-zinc-800">{SHORT_LINK_HOST}/&lt;slug&gt;</code> pattern.
				</p>
			</header>

			{!props.canQuery && (
				<p className="px-6 py-8 text-center text-sm text-zinc-500">
					Enter an influencer ID above to see their links.
				</p>
			)}

			{props.canQuery && props.error && (
				<p className="px-6 py-8 text-center text-sm text-red-600">{props.error}</p>
			)}

			{props.canQuery && !props.error && props.loading && sortedRows.length === 0 && (
				<p className="px-6 py-8 text-center text-sm text-zinc-500">
					Loading links.
				</p>
			)}

			{props.canQuery && !props.error && !props.loading && sortedRows.length === 0 && (
				<p className="px-6 py-8 text-center text-sm text-zinc-500">
					No links yet for this influencer.
				</p>
			)}

			{sortedRows.length > 0 && (
				<div className="overflow-x-auto">
					<table className="w-full table-auto text-left text-sm" aria-label="Influencer links">
						<thead className="text-xs uppercase tracking-[0.08em] text-zinc-500">
							<tr className="border-b border-zinc-200">
								<th scope="col" className="px-6 py-3 font-medium">Slug</th>
								<th scope="col" className="px-6 py-3 font-medium">Destination</th>
								<th scope="col" className="px-6 py-3 font-medium">Status</th>
								<th scope="col" className="px-6 py-3 text-right font-medium">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{sortedRows.map((row) => (
								<LinkRow
									key={row.id}
									row={row}
									busy={props.busy}
									onRevoke={props.onRevoke}
									onDelete={props.onDelete}
									revoking={props.pendingRevokeId === row.id}
									deleting={props.pendingDeleteId === row.id}
								/>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function LinkRow(props: {
	row: InfluencerLinkRow;
	busy: boolean;
	onRevoke: (id: string) => void;
	onDelete: (id: string) => void;
	revoking: boolean;
	deleting: boolean;
}) {
	const { row } = props;
	const revoked = isLinkRevoked(row);
	const shortUrl = buildShortUrl(row.slug);

	return (
		<tr
			data-link-id={row.id}
			data-revoked={revoked ? "true" : "false"}
			className={revoked ? "bg-zinc-50 text-zinc-400" : "text-zinc-800"}
		>
			<td className="px-6 py-3 align-top">
				<div className="flex flex-col gap-0.5">
					<code className={`font-mono text-sm ${revoked ? "text-zinc-400 line-through" : "text-zinc-900"}`}>
						{row.slug}
					</code>
					<span className={HINT}>{shortUrl}</span>
				</div>
			</td>
			<td className="px-6 py-3 align-top">
				<a
					href={row.dest_url}
					target="_blank"
					rel="noreferrer noopener"
					className={`break-all text-sm ${revoked ? "text-zinc-400" : "text-sky-700 hover:text-sky-800 hover:underline"}`}
				>
					{row.dest_url}
				</a>
			</td>
			<td className="px-6 py-3 align-top">
				<StatusBadge revoked={revoked} revokedAt={row.revoked_at} />
			</td>
			<td className="px-6 py-3 align-top">
				<div className="flex items-center justify-end gap-2">
					<CopyLinkButton shortUrl={shortUrl} disabled={revoked || props.busy} />
					<button
						type="button"
						className={SECONDARY_BTN}
						onClick={() => props.onRevoke(row.id)}
						disabled={revoked || props.busy}
						aria-label={`Revoke link ${row.slug}`}
					>
						{props.revoking ? "Revoking." : "Revoke"}
					</button>
					<button
						type="button"
						className={DANGER_BTN}
						onClick={() => props.onDelete(row.id)}
						disabled={props.busy}
						aria-label={`Delete link ${row.slug}`}
					>
						{props.deleting ? "Deleting." : "Delete"}
					</button>
				</div>
			</td>
		</tr>
	);
}

function StatusBadge(props: { revoked: boolean; revokedAt: number | null }) {
	if (props.revoked) {
		return (
			<span
				data-status="revoked"
				className="inline-flex items-center gap-1.5 rounded-[6px] border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"
				title={
					props.revokedAt
						? `Revoked ${new Date(props.revokedAt).toISOString()} (410 Gone)`
						: "Revoked (410 Gone)"
				}
			>
				<span
					aria-hidden="true"
					className="h-1.5 w-1.5 rounded-full bg-red-500"
				/>
				Revoked
			</span>
		);
	}
	return (
		<span
			data-status="active"
			className="inline-flex items-center gap-1.5 rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
		>
			<span
				aria-hidden="true"
				className="h-1.5 w-1.5 rounded-full bg-emerald-500"
			/>
			Active
		</span>
	);
}

function CopyLinkButton(props: { shortUrl: string; disabled?: boolean }) {
	const [copied, setCopied] = useState(false);

	async function handleCopy() {
		try {
			// Use the Clipboard API when available; fall back to a temporary
			// textarea for older browsers / non-secure contexts.
			if (
				typeof navigator !== "undefined" &&
				navigator.clipboard &&
				typeof navigator.clipboard.writeText === "function"
			) {
				await navigator.clipboard.writeText(props.shortUrl);
			} else if (typeof document !== "undefined") {
				const el = document.createElement("textarea");
				el.value = props.shortUrl;
				el.setAttribute("readonly", "true");
				el.style.position = "absolute";
				el.style.left = "-9999px";
				document.body.appendChild(el);
				el.select();
				document.execCommand("copy");
				document.body.removeChild(el);
			}
			setCopied(true);
			toast.success("Short link copied.");
			setTimeout(() => setCopied(false), 1500);
		} catch {
			toast.error("Copy failed — copy the link manually.");
		}
	}

	return (
		<button
			type="button"
			onClick={handleCopy}
			disabled={props.disabled}
			className={SECONDARY_BTN}
			aria-label={`Copy short link ${props.shortUrl}`}
		>
			{copied ? "Copied" : "Copy link"}
		</button>
	);
}

export default InfluencerLinkPage;
